import { useEffect, type RefObject } from "react";

export type PopoverDismissRef = RefObject<HTMLElement | null>;

export type UsePopoverDismissOptions = {
  open: boolean;
  refs: readonly PopoverDismissRef[];
  onClose: () => void;
  closeOnEscape?: boolean;
  closeOnOutsidePointerDown?: boolean;
};

export function usePopoverDismiss({
  open,
  refs,
  onClose,
  closeOnEscape = true,
  closeOnOutsidePointerDown = true,
}: UsePopoverDismissOptions) {
  useEffect(() => {
    if (!open) {
      return;
    }

    const handlePointerDown = (event: PointerEvent) => {
      if (!closeOnOutsidePointerDown) {
        return;
      }
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }
      if (refs.some((ref) => ref.current?.contains(target))) {
        return;
      }
      onClose();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (closeOnEscape && event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [closeOnEscape, closeOnOutsidePointerDown, onClose, open, refs]);
}
